/**
 * One group's own record — the owner, above all.
 *
 * A conversation row carries a group's members but not its owner or admins,
 * and that tier decides what the group menu offers. So this is called for the
 * open group only, rather than for every group in the list.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import type { ChatGroup } from "../types"
import { jidLocalPart, type Jid } from "../xmpp/jid"
import { groupManagedBy } from "./groupManagedBy"
import {
  useChatRuntime,
  useChatState,
  useIsChatAuthenticated,
  useMessages,
} from "./useChat"

export function useGroup(groupId: string | undefined): ChatGroup | undefined {
  const { actions } = useChatRuntime()
  const isAuthenticated = useIsChatAuthenticated()

  const { data } = useQuery({
    queryKey: ["chat", "group", groupId],
    queryFn: () => actions.getGroup(groupId ?? ""),
    enabled: isAuthenticated && groupId !== undefined,
  })

  return data
}

/** Owner or admin of the open group (FAME-574). */
export function useIsGroupManager(
  peer: Jid | undefined,
  groupId: string | undefined
): boolean {
  const queryClient = useQueryClient()
  const group = useGroup(groupId)
  const selfJid = useChatState((state) => state.selfJid)
  const ownHandle = selfJid ? jidLocalPart(selfJid) : undefined

  // A live promoted/demoted only reaches us by refetching the record — the
  // store carries no admins (FAME-574). Keyed on the newest message.
  const messages = useMessages(peer)
  const latestMessage = messages[messages.length - 1]
  useEffect(() => {
    if (
      groupId &&
      latestMessage?.type === "event" &&
      (latestMessage.event === "promoted" || latestMessage.event === "demoted")
    )
      void queryClient.invalidateQueries({
        queryKey: ["chat", "group", groupId],
      })
  }, [latestMessage, groupId, queryClient])

  return groupManagedBy(group, ownHandle)
}
